/**
 * Web dashboard localisation (i18next + react-i18next, the same stack the
 * vendored desktop UI uses, in a separate instance).
 *
 * - Catalogs live in ./locales/<code>.json; en.json is the reference and
 *   every other catalog must have exactly its keys (i18n-parity test).
 * - Adding a language = add its JSON file and one entry in LANGUAGES.
 * - The active language follows the user's "Interface language" preference
 *   (config key `language`, user scope), else the browser language, else
 *   English. Missing keys fall back to English.
 */
import i18next from "i18next"
import { initReactI18next } from "react-i18next"
import en from "./locales/en.json"
import it from "./locales/it.json"

export const LANGUAGES = [
  { code: "en", label: "English", locale: "en-GB" },
  { code: "it", label: "Italiano", locale: "it-IT" },
] as const

export type LangCode = (typeof LANGUAGES)[number]["code"]

/** A stored preference or browser language → a supported language, if any. */
export function toLang(value: unknown): LangCode | null {
  if (typeof value !== "string" || value.length < 2) return null
  const base = value.toLowerCase().split(/[-_]/)[0]
  return LANGUAGES.find((l) => l.code === base)?.code ?? null
}

/** The browser's first supported language, else English. */
export function browserDefault(): LangCode {
  if (typeof navigator === "undefined") return "en"
  for (const l of navigator.languages ?? [navigator.language]) {
    const code = toLang(l)
    if (code) return code
  }
  return "en"
}

const i18n = i18next.createInstance()
void i18n.use(initReactI18next).init({
  resources: { en: { web: en }, it: { web: it } },
  ns: ["web"],
  defaultNS: "web",
  lng: browserDefault(),
  fallbackLng: "en",
  interpolation: { escapeValue: false }, // React escapes already
  returnNull: false,
})

export default i18n

/** BCP-47 locale for dates and numbers in the given (or current) language. */
export function localeFor(lang: string = i18n.language): string {
  return LANGUAGES.find((l) => l.code === toLang(lang))?.locale ?? "en-GB"
}
